// Parameters of one page block (decision 023, stage I3): a control per parameter kind — text, number, choice, a section of the site,
// a field of that section, an event — for the builder's side panel.

import { useMemo } from 'react';
import { recordTitle, type BlockDef, type BlockParam, type PageBlock } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { BlockEditor } from '../editor/BlockEditor.tsx';
import { LINE_KINDS, SITE_TEXT_KINDS } from '../editor/kinds.ts';
import { Checkbox, Input, TextArea, Select, Field } from '../ui/primitives.tsx';
import s from './sites.module.css';

const str = (v: unknown) => (v == null ? '' : String(v));

export function BlockParams({ block, def, onChange }: { block: PageBlock; def: BlockDef; onChange: (key: string, value: unknown) => void }) {
  const client = useSync();
  const meta = useMeta();
  const sitePublic = client.header?.sitePublic ?? [];
  // Events of the scope selector: a block may show one event, or the event of the page or its site.
  const scopeType = client.header?.scope?.typeId;
  const events = useMemo(() => (scopeType ? [...client.store.recordIds(scopeType)].map((rid) => client.store.record(rid)).filter((r): r is NonNullable<typeof r> => !!r && !r.archivedAt)
    .map((r) => ({ value: r.id, label: recordTitle(r, meta) })) : []), [scopeType, client.store, meta]);

  const control = (p: BlockParam) => {
    const v = block.params[p.key];
    const set = (x: unknown) => onChange(p.key, x);
    const label = `${def.name}: ${p.label}`;
    // A text of the page is written as in the knowledge base (decision 034), with only the blocks the place can show.
    if (p.kind === 'richtext') return <BlockEditor key={`${block.id}:${p.key}`} compact value={str(v)} label={label} kinds={p.blocks === 'line' ? LINE_KINDS : SITE_TEXT_KINDS} onChange={(md) => set(md || undefined)} />;
    if (p.kind === 'textarea') return <TextArea rows={5} aria-label={label} value={str(v)} onChange={(e) => set(e.target.value)} />;
    if (p.kind === 'number') return <Input type="number" min={1} aria-label={label} value={str(v)} onChange={(e) => set(e.target.value ? Number(e.target.value) : undefined)} />;
    if (p.kind === 'checkbox') return <Checkbox label={label} checked={v === true} onChange={set} />;
    const opts = p.kind === 'select' ? p.options ?? []
      : p.kind === 'type' ? sitePublic.map((t) => ({ value: t.typeKey, label: t.name }))
      : p.kind === 'field' ? (sitePublic.find((t) => t.typeKey === str(block.params.type))?.fields ?? []).map((f) => ({ value: f.key, label: f.label }))
      : p.kind === 'scope' ? events : null;
    if (opts) return (
      <Select label={label} search={p.kind === 'scope'} value={str(v ?? p.default)} onChange={(x) => set(x || undefined)}
        options={[...(p.kind !== 'select' ? [{ value: '', label: p.kind === 'scope' ? 'Событие страницы или сайта' : '—' }] : []), ...opts]} />
    );
    return <Input aria-label={label} value={str(v)} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <div className={s.params}>
      <p className={s.help}>{def.description}</p>
      {def.params.map((p) => (
        <Field key={p.key} as={p.kind === 'richtext' ? 'div' : undefined} label={<>{p.label}</>}>{control(p)}{p.help && <span className={s.help}>{p.help}</span>}</Field>
      ))}
    </div>
  );
}
